import { EditorContent, type UseEditorOptions, useEditor } from '@tiptap/react'
import {
  type JSX,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { tiptapVariants } from '#src/components/core/tiptapVariants.ts'
import type { AIProposal } from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import {
  getSuggestionContentExtensions,
  suggestionPreviewHtml,
} from '#src/components/rich-text-editor/suggestionContentExtensions.ts'

export interface SuggestionContentPreviewProps {
  proposal: AIProposal
  /* Click-to-edit entry point. Receives the position the click maps to in THIS
   * preview's document, or null when the coordinate resolves to nothing. Absent
   * where no island exists (bare editors, stories), which leaves the card inert
   * as before. */
  onRequestEdit?: (caretPos: number | null) => void
}

/* How far (px) a release may sit from its press and still be the same click.
 * Sized for pointer jitter, not for drags: a real drag crosses it immediately
 * and is judged on where it was released instead. */
const CLICK_SLOP_PX = 4

interface PendingPress {
  // Resolved against the pre-dismiss layout, in the press handler.
  caretPos: number | null
  clientX: number
  clientY: number
  card: HTMLElement
}

/* Whether a release completes its press into a click on THIS card.
 *
 * A stationary pointer always counts, even when nothing sits under it any more:
 * dismissing an open island above this card pulls the card upward between press
 * and release, so the release coordinates can land past the card without the
 * clinician having moved at all. Judging that release by what is under it would
 * discard the very gesture this card was pressed for.
 *
 * Once the pointer HAS moved it is a drag, and then the card's live box is the
 * right test: a selection dragged inside the card still opens it, a drag out of
 * the card and released elsewhere opens nothing. */
function releaseCompletesClick(
  press: PendingPress,
  event: PointerEvent
): boolean {
  if (
    Math.abs(event.clientX - press.clientX) <= CLICK_SLOP_PX &&
    Math.abs(event.clientY - press.clientY) <= CLICK_SLOP_PX
  ) {
    return true
  }
  const rect = press.card.getBoundingClientRect()
  return (
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom
  )
}

/* The one render path for pending AI-suggestion content (design §1.2): a
 * non-editable editor over the shared suggestion-content extensions, so a
 * proposal previews through the SAME real node views the host editor and the
 * accepted note use — never a second innerHTML serializer that drifts (the
 * blank-line class this replaces). Mounted host-owned via ReactRenderer (see
 * suggestionWidgetShells.ts) so it renders inside the host React tree and
 * inherits its providers; that is what lets a replaceNode field preview
 * (choice / observation) render. */
export function SuggestionContentPreview({
  proposal,
  onRequestEdit,
}: SuggestionContentPreviewProps): JSX.Element {
  const html = suggestionPreviewHtml(proposal)
  const options = useMemo(
    () =>
      ({
        extensions: getSuggestionContentExtensions(),
        content: html,
        editable: false,
        parseOptions: {
          // ##PreserveWhitespace Mirror the host editor: keep spaces between
          // adjacent inline nodes from collapsing.
          preserveWhitespace: true,
        },
        editorProps: {
          attributes: { class: tiptapVariants({ noPlaceholder: true }) },
        },
      }) satisfies UseEditorOptions,
    [html]
  )
  const editor = useEditor(options)

  // Re-seed when a streamed op completes/grows after this preview first mounted.
  // useEditor applies changed options through Editor.setOptions, which does NOT
  // re-apply the `content` option to the document — so without this the preview
  // stays frozen at its mount-time (partial/empty) content while the proposal's
  // stored content is corrected to full. Read-only preview, so there is no user
  // edit to lose. The initial mount already seeds `html`, so skip that first run.
  const seededHtml = useRef(html)
  useEffect(() => {
    if (!editor || seededHtml.current === html) {
      return
    }
    seededHtml.current = html
    editor.commands.setContent(html, {
      emitUpdate: false,
      parseOptions: { preserveWhitespace: true },
    })
  }, [editor, html])

  /* Clicking the card is the island's entry point (it replaced the popover's
   * pencil). posAtCoords resolves the click against this preview's own view, so
   * the caret lands on the word the clinician aimed at rather than at the end
   * of the suggestion.
   *
   * The mapping runs on pointerdown, NOT on the click that follows it: an
   * already-open island dismisses itself from a document-level pointerdown, and
   * that unmount reflows the document, so by click time these same client
   * coordinates address different text. This element-level listener runs before
   * the document-level one, so the coordinates resolve against the layout the
   * clinician actually aimed at. Position 0 is treated as "no position" by
   * Tiptap's autofocus resolution, so floor at the first valid text position
   * instead of silently opening unfocused. */
  const pendingPress = useRef<PendingPress | null>(null)
  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>): void => {
      if (!onRequestEdit || !editor || event.button !== 0) {
        pendingPress.current = null
        return
      }
      const coords = editor.view.posAtCoords({
        left: event.clientX,
        top: event.clientY,
      })
      pendingPress.current = {
        caretPos: coords ? Math.max(coords.pos, 1) : null,
        clientX: event.clientX,
        clientY: event.clientY,
        card: event.currentTarget,
      }
    },
    [onRequestEdit, editor]
  )

  /* Opening completes on the release rather than on the click the browser
   * derives from it. That click is delivered to the common ancestor of the
   * press and release targets, both hit-tested live — so the same dismissal
   * reflow that moves this card upward retargets the click off the card
   * entirely and no island opens at all. The release is watched on the document
   * because it lands wherever the card USED to be, which is no longer this
   * element. A press that never releases into a click on this card still opens
   * nothing, and the captured position is consumed either way so no later
   * gesture inherits it. */
  useEffect(() => {
    if (!onRequestEdit) {
      return
    }
    const onPointerUp = (event: PointerEvent): void => {
      const press = pendingPress.current
      pendingPress.current = null
      if (press !== null && releaseCompletesClick(press, event)) {
        onRequestEdit(press.caretPos)
      }
    }
    const onPointerCancel = (): void => {
      pendingPress.current = null
    }
    document.addEventListener('pointerup', onPointerUp)
    document.addEventListener('pointercancel', onPointerCancel)
    return () => {
      document.removeEventListener('pointerup', onPointerUp)
      document.removeEventListener('pointercancel', onPointerCancel)
    }
  }, [onRequestEdit])

  return (
    /* React node views mount through EditorContent's Portals SIBLING, so a
     * chip's events propagate up the React tree without ever crossing the
     * content div that carries the handlers below. This wrapper is a React
     * ANCESTOR of both trees — the one place that can see a chip click.
     * display:contents keeps it out of layout.
     *
     * On a click-to-edit card a press means "open the island", never "follow a
     * link" — without this, a click on a condition chip rides its react-router
     * Link out of the note mid-review (the page's navigation guard is scoped
     * to active generation, so nothing blocks it). Capture-phase preventDefault
     * marks the event defaultPrevented before the Link's own handler runs,
     * which is the signal react-router honors. This deliberately does NOT make
     * a chip press open the island (the pointerdown handler below still cannot
     * see chip presses — they bypass the content div); it only removes the
     * harmful navigation half. */
    <div
      className="contents"
      onClickCapture={
        onRequestEdit
          ? (event) => {
              if ((event.target as Element).closest('a[href]')) {
                event.preventDefault()
              }
            }
          : undefined
      }
    >
      <EditorContent
        editor={editor}
        data-testid="suggestion-content-preview-editor"
        className={onRequestEdit ? 'cursor-text' : undefined}
        onPointerDown={handlePointerDown}
      />
    </div>
  )
}
